import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import InfraNav from './components/InfraNav';
import InfraFooter from './components/InfraFooter';
import InfraCTA from './components/InfraCTA';
import './infrastructure.css';

export default function InfrastructureAgents() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="infra-page" data-testid="infrastructure-agents">
      <InfraNav transparent={false} />

      {/* ══════ HERO ══════ */}
      <div className="infra-product-hero">
        <div className="infra-product-hero-text">
          <div className="infra-product-eyebrow" style={{ color: 'var(--pebbles-blue)' }}>AGENT WALLETS</div>
          <h1 className="infra-product-headline">Financial infrastructure for the agentic era.</h1>
          <p className="infra-product-desc">
            AI agents with their own wallets, spending rules, category restrictions, and full audit trails. Built for companies deploying autonomous systems.
          </p>
          <div className="infra-product-buttons">
            <Link to="/login" className="infra-btn-primary" data-testid="agents-get-started">Get started</Link>
            <a href="mailto:hello@pebbles.financial" className="infra-btn-secondary">Contact sales</a>
          </div>
        </div>
        <div className="infra-product-hero-mockup">
          <div className="infra-feature-card">
            <div className="infra-feature-card-inner">
              <div className="infra-mockup-row">
                <span className="infra-mockup-label">Procurement AI</span>
                <span className="infra-badge infra-badge-blue">Autonomous</span>
              </div>
              <div style={{ fontSize: 24, fontWeight: 600, color: 'var(--pebbles-dark)', marginBottom: 2 }}>₦142,800</div>
              <div style={{ fontSize: 11, color: 'var(--pebbles-gray-400)', marginBottom: 14 }}>of ₦500,000 monthly limit</div>
              <div style={{ width: '100%', height: 8, background: 'var(--pebbles-gray-200)', borderRadius: 4, marginBottom: 12 }}>
                <div style={{ height: 8, borderRadius: 4, background: 'var(--pebbles-blue)', width: '28%' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--pebbles-gray-500)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--pebbles-green)', flexShrink: 0 }} />
                  SaaS, supplies, cloud services allowed
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: 'var(--pebbles-gray-500)' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#E24B4A', flexShrink: 0 }} />
                  Entertainment, personal expenses blocked
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 1: Agent-as-Spender ══════ */}
      <div className="infra-feature">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(65,88,208,0.1)', color: 'var(--pebbles-blue)' }}>Agent-as-Spender</span>
            <h3 className="infra-feature-title">Let agents spend — within rules you define</h3>
            <p className="infra-feature-desc">
              Fund an agent wallet, set spending categories, daily and monthly limits, and approved vendors. The agent spends autonomously within its guardrails. Every transaction is logged.
            </p>
            <ul className="infra-feature-bullets infra-check-blue">
              <li>Per-agent spending limits (daily, monthly)</li>
              <li>Category restrictions (allow/block lists)</li>
              <li>Vendor whitelist support</li>
              <li>Real-time balance and spend tracking</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pebbles-gray-500)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 4 }}>Agent Spending Rules</div>
                {[
                  { rule: 'Monthly limit', value: '₦500,000', icon: '=' },
                  { rule: 'Daily limit', value: '₦100,000', icon: '=' },
                  { rule: 'Allowed categories', value: 'SaaS, Cloud, Office', icon: '✓' },
                  { rule: 'Blocked categories', value: 'Entertainment, Travel', icon: '✕' },
                  { rule: 'Approval above', value: '₦200,000', icon: '!' },
                ].map((r, i) => (
                  <div key={i} className="infra-data-row" style={{ borderRadius: 8, border: '1px solid var(--pebbles-gray-100)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ width: 20, height: 20, borderRadius: 4, background: 'rgba(65,88,208,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--pebbles-blue)', fontWeight: 700, flexShrink: 0 }}>{r.icon}</span>
                      <span style={{ fontSize: 12, color: 'var(--pebbles-dark)' }}>{r.rule}</span>
                    </div>
                    <span className="infra-data-value">{r.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 2: Agent-as-Collector ══════ */}
      <div className="infra-feature infra-feature--reverse infra-feature--gray">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(65,88,208,0.1)', color: 'var(--pebbles-blue)' }}>Agent-as-Collector</span>
            <h3 className="infra-feature-title">Agents that collect payments on your behalf</h3>
            <p className="infra-feature-desc">
              Deploy agents that invoice customers, collect payments, reconcile receipts, and escalate disputes. Each agent has its own collection wallet with full visibility.
            </p>
            <ul className="infra-feature-bullets infra-check-blue">
              <li>Automated invoicing and follow-ups</li>
              <li>Collection wallet with real-time balance</li>
              <li>Dispute escalation workflows</li>
              <li>Settlement to parent company wallet</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner">
                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--pebbles-gray-500)', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 12 }}>Collection Agent Activity</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[
                    { action: 'Invoice sent', target: 'Acme Corp', amount: '₦250,000', time: '2m ago' },
                    { action: 'Payment received', target: 'Beta Ltd', amount: '₦180,000', time: '15m ago' },
                    { action: 'Follow-up sent', target: 'Gamma Inc', amount: '₦95,000', time: '1h ago' },
                    { action: 'Settled to company', target: 'Internal', amount: '₦430,000', time: '3h ago' },
                  ].map((a, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: '#fff', borderRadius: 8, border: '1px solid var(--pebbles-gray-100)' }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--pebbles-dark)' }}>{a.action}</div>
                        <div style={{ fontSize: 9, color: 'var(--pebbles-gray-400)' }}>{a.target} · {a.time}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--pebbles-dark)' }}>{a.amount}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 3: Agent-as-Intermediary ══════ */}
      <div className="infra-feature">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(65,88,208,0.1)', color: 'var(--pebbles-blue)' }}>Agent-as-Intermediary</span>
            <h3 className="infra-feature-title">Agents that move money between parties</h3>
            <p className="infra-feature-desc">
              Escrow agents, payment routers, and settlement bots. Hold funds, release on conditions, split payments, and manage multi-party transactions autonomously.
            </p>
            <ul className="infra-feature-bullets infra-check-blue">
              <li>Escrow and conditional release</li>
              <li>Multi-party payment splitting</li>
              <li>Automated settlement schedules</li>
              <li>Full transaction audit trail</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card">
              <div className="infra-feature-card-inner">
                <div className="infra-stepper" style={{ marginBottom: 16 }}>
                  <div className="infra-step-box infra-step-box--active" style={{ borderColor: 'var(--pebbles-blue)', background: 'rgba(65,88,208,0.1)', color: 'var(--pebbles-blue)' }}>Receive</div>
                  <span className="infra-step-arrow">&rarr;</span>
                  <div className="infra-step-box">Hold</div>
                  <span className="infra-step-arrow">&rarr;</span>
                  <div className="infra-step-box">Verify</div>
                  <span className="infra-step-arrow">&rarr;</span>
                  <div className="infra-step-box">Release</div>
                </div>
                <div className="infra-data-row"><span className="infra-data-label">Escrow balance</span><span className="infra-data-value">₦1,200,000</span></div>
                <div className="infra-data-row"><span className="infra-data-label">Pending releases</span><span className="infra-data-value">3 transactions</span></div>
                <div className="infra-data-row"><span className="infra-data-label">Released today</span><span className="infra-data-value">₦850,000</span></div>
                <div className="infra-data-row"><span className="infra-data-label">Avg. hold time</span><span className="infra-data-value">2.4 hours</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════ FEATURE 4: Rules Engine ══════ */}
      <div className="infra-feature infra-feature--reverse infra-feature--gray">
        <div className="infra-feature-inner">
          <div className="infra-feature-text">
            <span className="infra-feature-label" style={{ background: 'rgba(65,88,208,0.1)', color: 'var(--pebbles-blue)' }}>Rules Engine</span>
            <h3 className="infra-feature-title">Programmable financial guardrails</h3>
            <p className="infra-feature-desc">
              Define rules in code or through the dashboard. Combine spending limits, category restrictions, time-based constraints, and approval chains to create precisely scoped agent permissions.
            </p>
            <ul className="infra-feature-bullets infra-check-blue">
              <li>Code-defined or dashboard-configured rules</li>
              <li>Time-based constraints (business hours only)</li>
              <li>Approval chains for large transactions</li>
              <li>Emergency kill switch</li>
            </ul>
          </div>
          <div className="infra-feature-mockup">
            <div className="infra-feature-card" style={{ padding: 0 }}>
              <div className="infra-code-mockup" style={{ margin: 0, borderRadius: 14 }}>
                <pre>
<span className="infra-syn-kw">const</span> <span className="infra-syn-def">agent</span> = <span className="infra-syn-kw">await</span> pebbles.agents{'\n'}{'  '}<span className="infra-syn-fn">.create</span>({'{'}{'\n'}{'    '}name: <span className="infra-syn-str">'procurement-bot'</span>,{'\n'}{'    '}wallet_type: <span className="infra-syn-str">'agent'</span>,{'\n'}{'    '}rules: {'{'}{'\n'}{'      '}monthly_limit: <span className="infra-syn-num">500000</span>,{'\n'}{'      '}categories: [<span className="infra-syn-str">'saas'</span>, <span className="infra-syn-str">'cloud'</span>],{'\n'}{'      '}require_approval_above: <span className="infra-syn-num">200000</span>,{'\n'}{'      '}active_hours: <span className="infra-syn-str">'09:00-18:00'</span>,{'\n'}{'      '}kill_switch: <span className="infra-syn-num">true</span>{'\n'}{'    '}{'}'}{'\n'}{'  '}{'}'});
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      <InfraCTA />
      <InfraFooter />
    </div>
  );
}
